import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { secureFetch } from '../utils/authInterceptor';

const SisConfigContext = createContext();

export const SisConfigProvider = ({ children, initialConfig }) => {
  const [uiConfig, setUiConfig] = useState({
    tabla_ui: {},
    formulario_ui: {},
    entorno: 'PRODUCCION',
    botones_ui: {},
    modales_ui: {},
    navegacion_ui: {},
    menu_ui: {},
    panel_ui: {},
    manifiesto: {}
  });

  // 🔥 Flag de control: previene fetches redundantes
  const hasFetchedRef = useRef(false);

  // ==========================================
  // EFECTO: Cargar sisconfig UNA SOLA VEZ
  // ==========================================
  useEffect(() => {
    //console.log('🔍 DIAGNÓSTICO: useEffect del Provider ejecutándose');
    //console.log('🔍 hasFetchedRef.current:', hasFetchedRef.current);

    // 1️⃣ Prevenir fetch redundante
    if (hasFetchedRef.current) {
      const entornoActual = initialConfig?.entorno?.toUpperCase() || 'PRODUCCION';
      
      if (entornoActual === 'DESARROLLO') {
        //console.log('⚠️ [DESARROLLO] Fetch redundante prevenido');
        toast.warning('⚠️ Fetch redundante prevenido', { autoClose: 2000 });
      } //else {
        //console.log('🔇 [PRODUCCIÓN] Fetch redundante prevenido (silencioso)');
        //}
      
      return;
    }

    // 2️⃣ Verificar token de autenticación
    const token = sessionStorage.getItem('mapasac_access_token');
    
    if (!token) {
      //console.log('⚠️ No hay token, saltando carga de sisconfig');
      return;
    }

    // 3️⃣ Marcar flag ANTES de iniciar fetch (previene race conditions)
    hasFetchedRef.current = true;
    //console.log('🔒 Flag marcado ANTES del fetch para prevenir duplicados');

    // 4️⃣ Cargar configuración desde backend
    //console.log('🔄 Cargando configuración UI completa (PRIMERA VEZ)...');

    const loadConfig = async () => {
      try {
        const response = await secureFetch('/api/sqlexec_generico/ui', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          console.error('❌ Error HTTP cargando sisconfig:', response.status);
          hasFetchedRef.current = false; // Resetear flag si falla
          return;
        }

        const data = await response.json();
      
        if (data.success && data.data) {
          //console.log('✅ Configuración UI cargada exitosamente');
        
          setUiConfig({
            tabla_ui: data.data.tabla_ui || {},
            formulario_ui: data.data.formulario_ui || {},
            entorno: data.data.entorno || 'PRODUCCION',
            botones_ui: data.data.botones_ui || {},
            modales_ui: data.data.modales_ui || {},
            navegacion_ui: data.data.navegacion_ui || {},
            menu_ui: data.data.menu_ui || {},
            panel_ui: data.data.panel_ui || {},
            manifiesto: data.data.manifiesto || {}
          });
        } else {
          console.error('❌ Respuesta inválida del servidor');
          hasFetchedRef.current = false; // Resetear flag si datos inválidos
        }
      } catch (error) {
        console.error('❌ Error de red cargando sisconfig:', error);
        hasFetchedRef.current = false; // Resetear flag si hay excepción
      }
    };

    loadConfig();
  }, []); // ✅ Dependencias vacías: solo ejecuta al montar

  // ==========================================
  // FUNCIÓN: Recargar manualmente (AdminPanel)
  // ==========================================
  const reloadUIConfig = async () => {
    try {
      const token = sessionStorage.getItem('mapasac_access_token');
    
      if (!token) {
        toast.error('No hay sesión activa');
        return;
      }

      //console.log('🔄 Recargando configuración UI manualmente...');

      const response = await secureFetch('/api/sqlexec_generico/ui', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        toast.error('Error recargando configuración UI');
        return;
      }

      const data = await response.json();
    
      if (data.success && data.data) {
        //console.log('✅ Nueva configuración UI recibida');
      
        setUiConfig({
          tabla_ui: data.data.tabla_ui || {},
          formulario_ui: data.data.formulario_ui || {},
          entorno: data.data.entorno || 'PRODUCCION',
          botones_ui: data.data.botones_ui || {},
          modales_ui: data.data.modales_ui || {},
          navegacion_ui: data.data.navegacion_ui || {},
          menu_ui: data.data.menu_ui || {},
          panel_ui: data.data.panel_ui || {},
          manifiesto: data.data.manifiesto || {}
        });
      
        toast.success('✅ Configuración UI actualizada', { autoClose: 1500 });
      } else {
        toast.error('Respuesta inválida del servidor');
      }
    } catch (error) {
      console.error('❌ Error recargando UI config:', error);
      toast.error('Error al recargar configuración');
    } 
  };

  // ==========================================
  // HELPERS: Verificar entorno
  // ==========================================
  const isDesarrollo = () => {
    return uiConfig?.entorno?.toUpperCase() === 'DESARROLLO';
  };

  const isProduccion = () => {
    return uiConfig?.entorno?.toUpperCase() === 'PRODUCCION';
  };

  // ==========================================
  // PROVIDER: Single source of truth
  // ==========================================
  return (
    <SisConfigContext.Provider value={{ 
      sisConfig: uiConfig,
      uiConfig,
      setUiConfig,
      reloadUIConfig,
      isDesarrollo,
      isProduccion,
      tabla_ui: uiConfig.tabla_ui || {},
      formulario_ui: uiConfig.formulario_ui || {},
      entorno: uiConfig.entorno || 'PRODUCCION',
      botones_ui: uiConfig.botones_ui || {},
      modales_ui: uiConfig.modales_ui || {},
      navegacion_ui: uiConfig.navegacion_ui || {},
      menu_ui: uiConfig.menu_ui || {},
      panel_ui: uiConfig.panel_ui || {},
      manifiesto: uiConfig.manifiesto || {}
    }}>
      {children}
    </SisConfigContext.Provider>
  );
};

// ==========================================
// HOOK: Acceso al contexto
// ==========================================
export const useSisConfig = () => {
  const context = useContext(SisConfigContext);
  if (!context) {
    throw new Error('useSisConfig debe usarse dentro de SisConfigProvider');
  }
  return context;
};